import { useState } from 'react';

import { ActionIcon } from '@mantine/core';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/shared/components/Icon';

import type { HelpArea } from './helpTarget';
import type { HelpTarget } from './helpRoutes';
import { INDEX_PAGE } from './markdown/paths';

/** Whether the person is already reading the manual: the button would lead where they are. */
function inHelp(pathname: string): boolean {
  return (
    pathname === '/ayuda' ||
    pathname.startsWith('/ayuda/') ||
    pathname === '/portal/ayuda' ||
    pathname === '/platform/ayuda'
  );
}

/**
 * The "?" of the console's header and the portal's: opens the section of the manual that explains
 * the screen the person is on. The map of addresses to sections is read from the manual itself
 * (`helpRoutes`), on the first click and not before, so the manual's text stays out of the main
 * bundle; a screen the manual does not name opens the index of the help instead.
 */
export function HelpButton({ area }: { area: HelpArea }) {
  const { t } = useTranslation('help');
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const [opening, setOpening] = useState(false);

  if (inHelp(pathname)) {
    return null;
  }

  const open = async () => {
    setOpening(true);
    let target: HelpTarget | null = null;
    try {
      const { resolveHelpTarget } = await import('./helpTarget');
      target = await resolveHelpTarget(pathname, area);
    } catch {
      // The manual could not be read: the help's index still opens, and says so if it cannot either.
    } finally {
      setOpening(false);
    }

    if (area === 'portal') {
      await navigate({ to: '/portal/ayuda', hash: target?.anchor });
    } else if (target === null || target.page === INDEX_PAGE) {
      await navigate({ to: '/ayuda', hash: target?.anchor });
    } else {
      await navigate({ to: '/ayuda/$', params: { _splat: target.page }, hash: target.anchor });
    }
  };

  return (
    <ActionIcon
      variant="subtle"
      color="gray"
      size="lg"
      aria-label={t('button.label')}
      title={t('button.label')}
      loading={opening}
      onClick={() => void open()}
    >
      <Icon name="help" />
    </ActionIcon>
  );
}
